import type { ReactNode } from 'react';
import { BANNED_CONTENT, MIN_AGE, MIN_BROADCAST_AGE } from '@zip2live/shared/safety';
import { Footer, Header, Link } from '../components/Brand.tsx';

export const X_URL = 'https://x.com/zip2live';
export const OWNER_URL = 'https://jzw.dev';
/** Forwarded by Cloudflare Email Routing to the team (docs/deployment/cloudflare.md). */
export const SUPPORT_EMAIL = 'support@zip2live.com';

export function InfoPage({ title, accent, intro, children }: { title: string; accent: string; intro: ReactNode; children: ReactNode }) {
  return (
    <div className="flex min-h-full flex-col">
      <Header />
      <main className="mx-auto w-full max-w-3xl flex-1 px-5 pt-8 pb-20 sm:px-8 sm:pt-14">
        <span className="speed-stripes" aria-hidden />
        <h1 className="speed-text mt-4 text-[clamp(2.5rem,8vw,3.75rem)] leading-[0.95]">
          {title} <span className="text-race">{accent}</span>
        </h1>
        <div className="mt-4 max-w-2xl text-lg leading-relaxed text-muted">{intro}</div>
        <div className="mt-10">{children}</div>
      </main>
      <Footer />
    </div>
  );
}

export function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="border-t border-line py-7">
      <h2 className="speed-text text-xl sm:text-2xl">{title}</h2>
      <div className="mt-3 space-y-3 leading-relaxed text-muted [&_a]:font-bold [&_a]:text-navy [&_a]:underline [&_a]:underline-offset-2 [&_b]:text-navy">{children}</div>
    </section>
  );
}

const Updated = () => <p className="text-sm font-semibold text-muted">Last updated October 21, 2026.</p>;

export const FAQS: [string, ReactNode][] = [
  ['Do I need an account to go live?', 'No. Tap GO LIVE and you’re streaming as a guest on that device. Sign in later with your email or phone if you want to keep your streams, use other devices or claim zip2live.com/yourname.'],
  ['How old do I need to be?', `You need to be ${MIN_AGE} or older to use ZIP2LIVE, and ${MIN_BROADCAST_AGE} or older to go live. The phone app asks before you first go live or chat.`],
  ['Do my viewers need an app or an account?', 'No. Anyone with your link watches in their browser on a phone or computer.'],
  ['Who can see my stream?', 'Anyone who has the link. Streams are unlisted unless you switch on Show on Live Now (signed-in creators only), which lists it publicly while you’re live. You can switch it either way mid-stream. Either way, we can’t control where a link gets shared, so treat it like a public post.'],
  ['How far behind live are viewers?', 'Usually about 2 to 3 seconds, depending on their connection and device.'],
  ['Can I stream from OBS?', <>Yes. Open <Link to="/obs">OBS setup</Link>, copy your server and stream key into OBS once, then press Start Streaming whenever you want to go live.</>],
  ['What happens when I end a stream?', 'Your link stays up, and the whole broadcast stays at the same link as a replay: for 24 hours if you streamed as a guest, or 30 days if you’re signed in. In Your streams you can make the replay private, so only you can watch it, or delete the stream.'],
  ['Does it cost anything?', 'Going live and watching are free.'],
  ['How do I report a stream?', <>Use “Report this stream” under any video, or tap a chat message and choose Report. A person reviews reports, and anything our automatic checks take down. If something you own was streamed without permission, send a <Link to="/copyright">copyright notice</Link>.</>],
  ['How do I block someone?', 'Tap one of their chat messages and choose Block, or use “Block” under their stream. You won’t see their streams on Live Now or their chat messages, and they can’t chat on your streams. Unblock them in Your streams.'],
  ['My stream was removed. Can I appeal?', 'Yes. In Your streams, a removed stream shows why and has an Appeal button. A person looks at every appeal. For a copyright removal you send a counter-notice instead.'],
  ['How do I delete my account?', 'In Your streams, scroll down and tap Delete account. Your streams, replays, chat messages, link and sign-in details are deleted straight away. Guests can delete their guest data the same way.'],
];

export function Faq() {
  return (
    <InfoPage title="Questions," accent="answered." intro="The short version: tap GO LIVE, share the link, people watch. Here’s the rest.">
      <dl>
        {FAQS.map(([q, a]) => (
          <div key={q} className="border-t border-line py-6">
            <dt className="text-lg font-extrabold">{q}</dt>
            <dd className="mt-2 leading-relaxed text-muted [&_a]:font-bold [&_a]:text-navy [&_a]:underline">{a}</dd>
          </div>
        ))}
      </dl>
      <p className="border-t border-line pt-6 text-muted">
        Still stuck? <Link to="/contact" className="font-bold text-navy underline underline-offset-2">Contact us</Link>.
      </p>
    </InfoPage>
  );
}

export function Contact() {
  return (
    <InfoPage title="Contact" accent="us." intro="Questions, feedback, partnerships or a problem with a stream. We read everything.">
      <div className="grid gap-4 sm:grid-cols-2">
        <a href={`mailto:${SUPPORT_EMAIL}`} className="rounded-3xl bg-race p-6 text-white hover:bg-race-600 sm:col-span-2">
          <p className="speed-text text-2xl">Email us</p>
          <p className="mt-1 font-bold break-all">{SUPPORT_EMAIL}</p>
        </a>
        <a href={X_URL} target="_blank" rel="noopener me" className="rounded-3xl bg-navy p-6 text-white hover:bg-navy-700">
          <p className="speed-text text-2xl">On X</p>
          <p className="mt-1 text-muted-dark">Message or mention @zip2live</p>
        </a>
        <a href={OWNER_URL} target="_blank" rel="noopener" className="rounded-3xl border-2 border-line p-6 hover:border-navy">
          <p className="speed-text text-2xl">The company</p>
          <p className="mt-1 text-muted">ZIP2LIVE is run by jzw.dev</p>
        </a>
      </div>
      <div className="mt-8" />
      <Section title="Reporting a stream">
        <p>Use “Report this stream” under the video. It reaches us with the stream attached, which is the fastest way to get something reviewed.</p>
      </Section>
      <Section title="Copyright">
        <p>If a stream uses your copyrighted work without permission, send us a <Link to="/copyright">copyright notice</Link>.</p>
      </Section>
    </InfoPage>
  );
}

export function Privacy() {
  return (
    <InfoPage title="Privacy" accent="policy." intro="What ZIP2LIVE collects, why, and what you control.">
      <Updated />
      <Section title="What we collect">
        <p><b>Guests:</b> a random guest ID stored in a cookie on your device, and the streams you start.</p>
        <p><b>Viewers:</b> a random ID stored in your browser, so a stream’s view count counts you once.</p>
        <p><b>Signed-in creators:</b> the email address or phone number you verify, your display name and personal link if you choose them, and your streams.</p>
        <p><b>Followers:</b> the channels you follow and, if you turn on alerts, this browser’s notification address (from your browser’s push service). Email and text alerts only go to the address or number you verified, and every alert has a link to stop them.</p>
        <p><b>The phone app:</b> before you first go live or chat, the app asks your age and for you to agree to the rules. We keep your age group (13 to 17, or 18 and over) and when you agreed, never your birth date. If you allow alerts, we keep your phone’s push token so we can send the go-live alerts you ask for.</p>
        <p><b>Everyone:</b> basic technical logs (such as IP address and browser) used to run the service, prevent abuse and rate-limit sign-in codes.</p>
      </Section>
      <Section title="Camera, microphone and your Lock Screen">
        <p>The app and the website use your camera and microphone only while you’re on the Go Live screen. What you broadcast goes to your viewers and is kept as a replay, as below.</p>
        <p>While you’re live, the iPhone app can show a card on your Lock Screen and in the Dynamic Island with how long you’ve been live, your viewer count and your link. It is drawn on your phone and sends nothing extra.</p>
      </Section>
      <Section title="Safety checks">
        <p>Every live stream is checked automatically every few minutes, and so are moments viewers report and profile photos you upload, by an AI model that runs on Cloudflare. The short clips it checks are kept privately for staff to review and deleted after 90 days. Chat messages, chat names, stream titles, channel names and bios are checked against a list of slurs and sexual words. Reports and the reported content go to our staff.</p>
      </Section>
      <Section title="Your streams">
        <p>Video you broadcast is delivered to anyone with your link and is kept as a replay for 24 hours (guests) or 30 days (signed-in creators). Links are unlisted, not private, and a stream you list on Live Now can be found by anyone while it’s live. You can make a replay private or delete it in Your streams.</p>
      </Section>
      <Section title="How we use it">
        <p>To run ZIP2LIVE: delivering streams, sending your sign-in codes and the go-live alerts you ask for, keeping your streams with your account, and preventing abuse. We don’t sell your personal information.</p>
      </Section>
      <Section title="Services we use">
        <p>Cloudflare (hosting, storage, email and the AI safety checks), Fly.io (video processing), an SMS provider for phone codes and text alerts, your browser’s push service for browser alerts, and Expo’s push service, which passes the phone app’s alerts to Apple or Google.</p>
      </Section>
      <Section title="Deleting your account">
        <p>
          Delete your account (or your guest data) any time in <Link to="/account">Your streams</Link>, on the website or in the app. It happens straight away: your streams and replays, chat name and messages, link, mods, bans, blocks, follows and alert settings, contact details and sign-in details are deleted. Your @link is retired for good, so nobody else can ever claim it.
        </p>
        <p>
          We keep, without your account: reports about streams and messages (with the reported text), private clips our automatic moderation reviewed (deleted after 90 days), copyright notices and staff decisions, as long as we need them to keep ZIP2LIVE safe and meet legal obligations.
        </p>
      </Section>
      <Section title="Children">
        <p>ZIP2LIVE isn’t for children under {MIN_AGE}, and we don’t knowingly collect their information. If you think a child under {MIN_AGE} is using ZIP2LIVE, <Link to="/contact">contact us</Link> and we’ll delete their data.</p>
      </Section>
      <Section title="Questions">
        <p>
          Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or <Link to="/contact">contact us</Link> to ask about your data.
        </p>
      </Section>
    </InfoPage>
  );
}

export function Terms() {
  return (
    <InfoPage title="Terms" accent="of use." intro={<>By using ZIP2LIVE you agree to these terms. ZIP2LIVE is operated by <a href={OWNER_URL} target="_blank" rel="noopener" className="font-bold text-navy underline">jzw.dev</a>.</>}>
      <Updated />
      <Section title="Zero tolerance">
        <p>
          ZIP2LIVE has <b>zero tolerance for objectionable content and abusive users</b>. This covers everything you put on ZIP2LIVE: live video and sound, titles, chat messages, names, profile photos, bios and links. Never stream, post or say:
        </p>
        <ul className="list-disc space-y-1 pl-5">
          {BANNED_CONTENT.map((rule) => <li key={rule}>{rule}</li>)}
          <li>Impersonating someone, spam or scams, or anything that attacks or overloads the service</li>
        </ul>
      </Section>
      <Section title="Who can use ZIP2LIVE">
        <p>You must be {MIN_AGE} or older to use ZIP2LIVE, and {MIN_BROADCAST_AGE} or older to go live. The phone app asks your age, and for you to agree to these rules, before you first go live or chat.</p>
      </Section>
      <Section title="Your content">
        <p>You own what you stream. You’re responsible for it and must have the rights to everything in it, including music and video from others.</p>
      </Section>
      <Section title="How we enforce these rules">
        <p>Every live stream is checked automatically, and chat messages, names and titles are filtered for slurs and sexual content. Anyone can report a stream or a chat message, and block anyone, on the website or in the app.</p>
        <p>We review reports within 24 hours. When something breaks these rules we remove it, end the stream and suspend the account that posted it (guest accounts too), with or without notice, and report it to the authorities where the law requires. Automatic checks can end a live stream on their own; a person then reviews it.</p>
        <p>You can appeal a removal or a suspension from <Link to="/account">Your streams</Link>. A person reviews every appeal.</p>
      </Section>
      <Section title="Copyright">
        <p>
          Don’t stream content you don’t have the rights to. Rights holders can send us a <Link to="/copyright">copyright notice</Link>; if we remove a stream, its creator can send a counter-notice. Accounts with three streams removed for copyright within a year are suspended.
        </p>
      </Section>
      <Section title="The service">
        <p>ZIP2LIVE is provided as is, without guarantees of availability. Features may change.</p>
      </Section>
      <Section title="Questions">
        <p>
          Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a>, <Link to="/contact">contact us</Link>, or see the <Link to="/privacy">privacy policy</Link>.
        </p>
      </Section>
    </InfoPage>
  );
}

/** Where deleting your account lands. */
export function AccountDeleted() {
  return (
    <InfoPage title="Account" accent="deleted." intro="Your streams, replays, chat messages and account details are gone. Thanks for streaming with us.">
      <Section title="Start again any time">
        <p>
          You don’t need an account to go live. <Link to="/go-live">Go live</Link> as a guest, or <Link to="/signin">sign in</Link> with your email or phone to make a new account.
        </p>
      </Section>
    </InfoPage>
  );
}
